/**
 * socialLogoSets.service.ts
 *
 * Typed API call wrappers for the Social Logo Sets domain.
 * A "set" is a themed collection of hosted PNG icons — one URL per social
 * platform — used by the DnD email editor's social blocks.
 *
 * All methods use the shared apiClient (Axios instance with JWT interceptor)
 * and return unwrapped data from the response envelope.
 */
import { apiClient } from './apiClient';
import type { ApiResponse } from '@/types/api.types';

// ─── Domain types ─────────────────────────────────────────────────────────────

/**
 * A social logo set as returned by GET /social-logo-sets. Each `*Logo` field
 * holds the hosted PNG URL for that platform (or empty/null when the set has
 * no icon for it). Columns mirror the `social_logo_sets` table.
 */
export interface SocialLogoSet {
  socialLogoSetID: number;
  name: string;
  description: string | null;
  facebookLogo: string | null;
  instagramLogo: string | null;
  xLogo: string | null;
  linkedinLogo: string | null;
  youtubeLogo: string | null;
  tiktokLogo: string | null;
  pinterestLogo: string | null;
  websiteLogo: string | null;
  emailLogo: string | null;
  active: number;
  setOrder: number;
}

// ─── API methods ──────────────────────────────────────────────────────────────

/**
 * Fetch all active social logo sets for the account, ordered by setOrder.
 */
export function listSocialLogoSets(): Promise<SocialLogoSet[]> {
  return apiClient
    .get<ApiResponse<SocialLogoSet[]>>('/social-logo-sets')
    .then((r) => r.data.data);
}
